import { useState, useEffect, useCallback } from "react";
import { disruptionAPI } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import { toast } from "sonner";
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, ZAxis, Cell,
} from "recharts";
import {
  Trophy, Download, Lightbulb, Loader2, User, Clock, Send, Users,
  ChevronDown, ChevronUp, Star, FileText,
} from "lucide-react";

const CATEGORY_COLORS = {
  business_model: "bg-violet-50 text-violet-700 border-violet-200",
  product: "bg-indigo-50 text-indigo-700 border-indigo-200",
  service: "bg-emerald-50 text-emerald-700 border-emerald-200",
  process: "bg-amber-50 text-amber-700 border-amber-200",
  region: "bg-sky-50 text-sky-700 border-sky-200",
};

const SCATTER_COLORS = {
  business_model: "#8B5CF6", product: "#6366F1", service: "#10B981",
  process: "#F59E0B", region: "#0EA5E9",
};

const EFFORT_MAP = { low: 1, medium: 2, high: 3 };
const IMPACT_MAP = { low: 1, medium: 2, high: 3 };

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.[0]) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-lg p-3 max-w-[240px]">
      <div className="font-heading font-semibold text-sm text-slate-900">{d.title}</div>
      <div className="text-[10px] text-slate-400 mt-0.5">{d.submitted_by_name}</div>
      <div className="flex items-center gap-2 mt-1.5">
        <Badge variant="outline" className="text-[9px]">Effort: {d.effortLabel}</Badge>
        <Badge variant="outline" className="text-[9px]">Impact: {d.impactLabel}</Badge>
        <span className="text-[9px] font-mono text-indigo-600">{d.vote_count} votes</span>
      </div>
    </div>
  );
};

const DisruptionResults = ({ boardId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  const fetchResults = useCallback(async () => {
    try {
      const res = await disruptionAPI.exportSession(boardId);
      setData(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [boardId]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  const addNote = async () => {
    if (!noteText.trim() || !selectedIdea) return;
    setAddingNote(true);
    try {
      const res = await disruptionAPI.addNote(boardId, selectedIdea.id, noteText);
      setData((prev) => ({
        ...prev,
        ideas: prev.ideas.map((i) => i.id === selectedIdea.id ? { ...i, notes: [...(i.notes || []), res.data] } : i),
      }));
      setSelectedIdea((prev) => ({ ...prev, notes: [...(prev.notes || []), res.data] }));
      setNoteText("");
      toast.success("Note added");
    } catch { toast.error("Failed"); }
    finally { setAddingNote(false); }
  };

  const exportJSON = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = "radical-disruption-export.json"; a.click(); URL.revokeObjectURL(url);
    toast.success("Exported!");
  };

  const downloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const res = await disruptionAPI.downloadPDF(boardId);
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `USG-Full-Report.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch (err) {
      toast.error("Failed to generate PDF");
    } finally {
      setDownloadingPDF(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!data) return <div className="text-center py-12 text-slate-400">No data available</div>;

  const topIdeas = data.top_ideas || [];
  const allIdeas = data.ideas || [];
  const responses = data.responses || [];
  const participants = data.participants || [];
  const p1Responses = responses.filter((r) => r.phase === "phase1");
  const p2Responses = responses.filter((r) => r.phase === "phase2");

  // Scatter chart data
  const scatterData = allIdeas
    .filter((i) => i.effort && i.impact)
    .map((i) => ({
      ...i,
      x: EFFORT_MAP[i.effort] || 2,
      y: IMPACT_MAP[i.impact] || 2,
      z: Math.max((i.vote_count || 0) * 40 + 60, 60),
      effortLabel: i.effort,
      impactLabel: i.impact,
    }));

  return (
    <div className="flex h-full" data-testid="disruption-results">
      {/* Participants Sidebar */}
      <aside className="w-56 bg-white border-r border-slate-200 hidden md:flex flex-col shrink-0">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <Users className="w-4 h-4 text-indigo-500" />
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Participants</span>
        </div>
        <ScrollArea className="flex-1">
          <div className="py-2">
            {participants.map((p, idx) => (
              <div key={idx} className="flex items-center gap-2.5 px-4 py-2" data-testid={`participant-${idx}`}>
                <div className="w-7 h-7 bg-indigo-100 rounded-full flex items-center justify-center text-[10px] font-bold text-indigo-700 shrink-0">
                  {(p.name || "?")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-slate-700 truncate">{p.name}</div>
                  <div className="text-[10px] text-slate-400 truncate">{p.email}</div>
                </div>
                <Badge className={`text-[8px] shrink-0 ${
                  p.role === "owner" ? "bg-indigo-50 text-indigo-600 border-indigo-200" : "bg-slate-100 text-slate-500 border-slate-200"
                }`}>
                  {p.role === "owner" ? "Facilitator" : "Player"}
                </Badge>
              </div>
            ))}
          </div>
        </ScrollArea>
        <div className="px-4 py-3 border-t border-slate-100">
          <div className="text-[10px] text-slate-400">{participants.length} participant(s)</div>
        </div>
      </aside>

      {/* Main Results */}
      <div className="flex-1 overflow-y-auto p-5">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading font-bold text-2xl text-slate-900 flex items-center gap-2">
                <Trophy className="w-6 h-6 text-amber-500" />Session Results
              </h2>
              <p className="text-sm text-slate-400 mt-1">{p1Responses.length} Phase 1 + {p2Responses.length} Phase 2 responses, {allIdeas.length} ideas</p>
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid="download-pdf-btn" onClick={downloadPDF} disabled={downloadingPDF} className="bg-indigo-600 hover:bg-indigo-700 text-white h-9 text-sm">
                {downloadingPDF ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />}
                {downloadingPDF ? "Generating..." : "Download PDF Report"}
              </Button>
              <Button data-testid="export-results-btn" onClick={exportJSON} variant="outline" className="h-9 text-sm">
                <Download className="w-4 h-4 mr-1.5" />Export JSON
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Phase 1 Responses", value: p1Responses.length, color: "text-red-600 bg-red-50" },
              { label: "Phase 2 Responses", value: p2Responses.length, color: "text-indigo-600 bg-indigo-50" },
              { label: "Ideas Generated", value: allIdeas.length, color: "text-amber-600 bg-amber-50" },
              { label: "Ideas Voted On", value: allIdeas.filter((i) => i.vote_count > 0).length, color: "text-violet-600 bg-violet-50" },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">{s.label}</div>
                <div className={`font-heading font-bold text-2xl ${s.color.split(" ")[0]}`}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Effort vs Impact Scatter Chart */}
          {scatterData.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
              <h3 className="font-heading font-semibold text-base text-slate-900 mb-4">Effort vs Impact Matrix</h3>
              <ResponsiveContainer width="100%" height={320}>
                <ScatterChart margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis
                    type="number" dataKey="x" domain={[0.5, 3.5]} ticks={[1, 2, 3]}
                    tickFormatter={(v) => ["", "Low", "Medium", "High"][v] || ""}
                    label={{ value: "Effort", position: "bottom", offset: 10, style: { fontSize: 12, fill: "#64748B" } }}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    type="number" dataKey="y" domain={[0.5, 3.5]} ticks={[1, 2, 3]}
                    tickFormatter={(v) => ["", "Low", "Medium", "High"][v] || ""}
                    label={{ value: "Impact", angle: -90, position: "insideLeft", offset: 5, style: { fontSize: 12, fill: "#64748B" } }}
                    tick={{ fontSize: 11 }}
                  />
                  <ZAxis type="number" dataKey="z" range={[60, 300]} />
                  <RTooltip content={<CustomTooltip />} />
                  <Scatter data={scatterData}>
                    {scatterData.map((entry, i) => (
                      <Cell key={i} fill={SCATTER_COLORS[entry.category] || "#6366F1"} fillOpacity={0.7} stroke={SCATTER_COLORS[entry.category] || "#6366F1"} strokeWidth={1} cursor="pointer" onClick={() => setSelectedIdea(entry)} />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-4 mt-2 justify-center">
                {Object.entries(SCATTER_COLORS).map(([k, v]) => (
                  <span key={k} className="flex items-center gap-1.5 text-[10px] text-slate-500">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: v }} />{k.replace("_", " ")}
                  </span>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 text-center mt-1">Bubble size = vote count. Click a bubble to view idea details.</p>
            </div>
          )}

          {/* Top Ideas */}
          {topIdeas.length > 0 && (
            <div className="mb-8">
              <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-500" />Innovation Shortlist
              </h3>
              <div className="space-y-3">
                {topIdeas.map((idea, idx) => (
                  <div key={idea.id} onClick={() => setSelectedIdea(idea)}
                    className={`bg-white rounded-xl border p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow ${idx === 0 ? "border-amber-300 shadow-sm" : "border-slate-200"}`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-heading font-bold text-lg ${
                      idx === 0 ? "bg-amber-100 text-amber-700" : idx === 1 ? "bg-slate-200 text-slate-600" : "bg-slate-100 text-slate-500"
                    }`}>{idx + 1}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-heading font-semibold text-sm text-slate-900">{idea.title}</h4>
                        <Badge className={`text-[10px] border ${CATEGORY_COLORS[idea.category] || ""}`}>{idea.category?.replace("_", " ")}</Badge>
                      </div>
                      {idea.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{idea.description}</p>}
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-[10px] text-slate-400 flex items-center gap-1"><User className="w-2.5 h-2.5" />{idea.submitted_by_name}</span>
                        <span className="text-[10px] text-slate-300 flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{formatDateTime(idea.created_at)}</span>
                        {(idea.notes?.length || 0) > 0 && <span className="text-[10px] text-indigo-500">{idea.notes.length} notes</span>}
                      </div>
                    </div>
                    <div className="text-center shrink-0">
                      <div className="font-mono text-xl font-bold text-indigo-600">{idea.vote_count}</div>
                      <div className="text-[10px] text-slate-400">votes</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All Ideas */}
          {allIdeas.length > 0 && (
            <div>
              <h3 className="font-heading font-semibold text-base text-slate-900 mb-3">All Ideas ({allIdeas.length})</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {allIdeas.map((idea) => (
                  <div key={idea.id} onClick={() => setSelectedIdea(idea)} className="bg-white rounded-lg border border-slate-200 p-3 cursor-pointer hover:shadow-sm transition-shadow">
                    <Badge className={`text-[9px] border mb-1.5 ${CATEGORY_COLORS[idea.category] || ""}`}>{idea.category?.replace("_", " ")}</Badge>
                    <h4 className="font-heading font-medium text-xs text-slate-800">{idea.title}</h4>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[10px] text-slate-400 flex items-center gap-1"><User className="w-2.5 h-2.5" />{idea.submitted_by_name}</span>
                      <span className="font-mono text-xs font-bold text-slate-500">{idea.vote_count || 0} votes</span>
                    </div>
                    <span className="text-[9px] text-slate-300">{formatDateTime(idea.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Idea Detail Dialog */}
      <Dialog open={!!selectedIdea} onOpenChange={() => setSelectedIdea(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="idea-detail-dialog">
          {selectedIdea && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-lg pr-6">{selectedIdea.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`text-[10px] border ${CATEGORY_COLORS[selectedIdea.category] || ""}`}>{selectedIdea.category?.replace("_", " ")}</Badge>
                  {selectedIdea.impact && <Badge variant="outline" className="text-[10px]">Impact: {selectedIdea.impact}</Badge>}
                  {selectedIdea.effort && <Badge variant="outline" className="text-[10px]">Effort: {selectedIdea.effort}</Badge>}
                  <Badge variant="outline" className="text-[10px] font-mono">{selectedIdea.vote_count || 0} votes</Badge>
                </div>

                {selectedIdea.description && <p className="text-sm text-slate-600 leading-relaxed">{selectedIdea.description}</p>}

                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <span className="flex items-center gap-1"><User className="w-3 h-3" />Created by: {selectedIdea.submitted_by_name}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDateTime(selectedIdea.created_at)}</span>
                </div>

                {/* Notes */}
                <div className="border-t border-slate-100 pt-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 block">Notes & Additional Ideas ({(selectedIdea.notes || []).length})</span>
                  <div className="space-y-2 mb-3 max-h-[200px] overflow-y-auto">
                    {(selectedIdea.notes || []).length === 0 && <p className="text-xs text-slate-300 italic">No notes yet. Be the first to add one.</p>}
                    {(selectedIdea.notes || []).map((note) => (
                      <div key={note.id} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                        <p className="text-sm text-slate-700">{note.text}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-[10px] text-indigo-600 font-medium">{note.user_name}</span>
                          <span className="text-[10px] text-slate-300">{formatDateTime(note.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid="idea-detail-note-input"
                      placeholder="Add a note..."
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && noteText.trim()) addNote(); }}
                      className="h-9 bg-white flex-1 text-sm"
                    />
                    <Button data-testid="idea-detail-add-note-btn" onClick={addNote} disabled={addingNote || !noteText.trim()} size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white h-9 px-3">
                      <Send className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DisruptionResults;
